<template lang="pug">
.pagination-container
  .limit-container
    label Rows per page
    aiq-select.page-size-selector(placeholder="Select"
                     size="small"
                     v-model="selectedOption")
            aiq-option(v-for="option in pageSizeOptions"
                      :value="option")
  .totals {{ totals }}
  .offset-container
    aiq-button.nav-btn(size="small"
              :disabled="requestedPageNumber <= 1"
              @click="fetchPage(requestedPageNumber - 1)")
      i.el-icon-arrow-left
    aiq-input.offset-input(size="small"
              type="text"
              :min="1"
              :max="totalPageCount"
              v-model="pageInput"
              :formatter="digitsOnly"
              :parser="digitsOnly"
              @keyup.enter="fetchPage(requestedPageNumber)")
    .page-count {{ `of ${totalPageCount}` }}
    aiq-button.nav-btn(size="small"
              :disabled="requestedPageNumber >= totalPageCount"
              @click="fetchPage(requestedPageNumber + 1)")
      i.el-icon-arrow-right
</template>

<script>
const PAGE_SIZE_OPTION_COUNT = 10;
const PAGE_SIZE_STEP = 100;

export default {
  name: 'bottom-pagination',
  props: {
    pageSize: {
      type: Number,
      required: true,
    },
    pagination: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      pageInput: this.digitsOnly(this.getPageNumberFromPagination()),
    };
  },
  computed: {
    selectedOption: {
      get() {
        return this.pageSize;
      },
      set(value) {
        this.$emit('update:pageSize', value);
        this.$emit('page-size-change');
      },
    },
    pageSizeOptions() {
      return Array.from({ length: PAGE_SIZE_OPTION_COUNT }, (_, index) => (index + 1) * PAGE_SIZE_STEP);
    },
    currentPageNumber() {
      if (!this.pagination.limit) {
        return 1;
      }

      return Math.floor(this.pagination.offset / this.pagination.limit) + 1;
    },
    requestedPageNumber() {
      return Number(this.pageInput) || this.currentPageNumber;
    },
    totalPageCount() {
      if (!this.pagination.limit) {
        return 1;
      }

      return Math.ceil(this.pagination.rowCount / this.pagination.limit) || 1;
    },
    totals() {
      return `${this.pagination.offset + 1} - ${Math.min(this.pagination.offset + this.pagination.limit, this.pagination.rowCount)} of ${this.pagination.rowCount}`;
    },
  },
  watch: {
    'pagination.offset': {
      handler() {
        this.pageInput = this.digitsOnly(this.currentPageNumber);
      },
    },
    'pagination.limit': {
      handler() {
        this.pageInput = this.digitsOnly(this.currentPageNumber);
      },
    },
  },
  methods: {
    digitsOnly(value) {
      return String(value).replace(/[^0-9]/g, '');
    },
    getPageNumberFromPagination() {
      if (!this.pagination.limit) {
        return 1;
      }

      return Math.floor(this.pagination.offset / this.pagination.limit) + 1;
    },
    getBoundedPageNumber(pageNumber) {
      return Math.min(Math.max(pageNumber, 1), this.totalPageCount);
    },
    fetchPage(pageNumber) {
      const boundedPageNumber = this.getBoundedPageNumber(pageNumber);
      this.pageInput = this.digitsOnly(boundedPageNumber);
      this.$emit('update:offset', (boundedPageNumber - 1) * this.pagination.limit);
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/styles/style-guide-light-theme.scss';

.pagination-container {
  margin-top: 10px;
  display: flex;
  justify-content: center;

  color: $aiq-theme-text-secondary;
  font-size: 12px;
  font-weight: 400;

  .limit-container {
    flex: 2;
    display: flex;
    justify-content: flex-end;
    align-items: center;

    label {
      margin-right: 20px;
    }

    .page-size-selector {
      width: 75px;
    }
  }

  .totals {
    flex: 1.5;
    align-self: center;
    text-align: center;
  }

  .offset-container {
    flex: 2;
    display: flex;
    align-items: center;

    .nav-btn {
      color: $aiq-theme-text-secondary;
      border: 1px solid $aiq-theme-border-elevated;

      &:focus {
        border: 1px solid $aiq-theme-border-elevated;
        outline: none;
      }

      &:hover {
        background-color: $aiq-theme-background-surface;
        border: 1px solid $aiq-theme-border-elevated;
      }

      &:active {
        background-color: $aiq-theme-background-elevated;
        border: 1px solid $aiq-theme-border-elevated;
      }

      &[disabled] {
        border-color: #fff;
        background-color: #fff;
        color: $aiq-theme-text-disabled;
      }
    }

    .offset-input {
      width: 50px;
      margin: 0 10px;
    }

    .page-count {
      margin-right: 10px;
    }
  }
}
</style>
